import React, {useCallback, useEffect, useLayoutEffect, useMemo} from "react";
import {routes, routesLists} from "../../core/models/static/routes/app-routes";
import {Redirect, Route, Switch, useHistory} from "react-router-dom";
import LocalStorageService from "../../core/services/cache/local_storage_service";
import NotFoundView from "./error/not-found";
import AccessDeniedView from "./error/access-denied";
import ServerErrorView from "./error/server-error";
import Api from "../../core/services/api/api";
import PrintViews from "./print";
import FirebaseNotificationService from "../../core/services/firebase";
import ConfirmationDialog from "../components/dialogs/confirmation";
import EnvService from "../../core/services/env";
import TestView from "./test";
import PanelViews from "./panel";
import CacheService from "../../core/services/cache/cache_service";
import ExportingDialog from "../components/dialogs/exporting";
import {DataGridProvider} from '../containers/data-grid'
import {QueryBuilderProvider} from "../../packages/query-builder";
import {injectBizFormApiInterceptors} from "../../packages/biz-form";
import {CachedIndustryProvider, CachedIndustrySchemaProvider, CachedPropertyGroupsProvider} from "../hooks/use-industry";
import {CachedCountryProvinceProvider} from "../hooks/use-country-province-cache";
import {CachedPriceLevelsDictionaryProvider} from "../hooks/use-price-level-dictionary";
import {PartnerConfigurationProvider} from "../hooks/use-partner-configuration";
import {AccessLevelsProvider} from "../hooks/use-access-levels";
import StripeProvider from "../hooks/use-stripe";
import BizProductionApiService from "../../core/services/api_service/production_api_service";
import PublicViews from "./public";
import {PurchasingKeyProvider} from "../hooks/use-purchasing-key";
import {LoadingView} from "./loading";
import {AuthenticationViews} from "./auth/base";
import {usePartnerInformation} from "../hooks/use-partner-information";
import {useAuthenticationState} from "../hooks/use-auth-state";
import {useOperatorInformation} from "../hooks/use-operator-information";

const ApplicationViews = () => {
    return (
        <>
            <Switch>
                <Route path={routesLists.public} exact>
                    <PublicViews/>
                </Route>
                <Route path={routes.landing}>
                    <PrivateRoutesViews/>
                </Route>
            </Switch>
        </>
    );
}

const PrivateRoutesViews = () => {
    const history = useHistory();
    const {authenticationToken, loggedIn} = useAuthenticationState();
    const partnerInformation = usePartnerInformation();
    const operatorInformation = useOperatorInformation();

    const partnerId = useMemo(() => partnerInformation?.id ?? CacheService.getOperatorPartnerId(), [partnerInformation?.id])
    const operatorId = useMemo(() => operatorInformation?.id ?? CacheService.getOperatorId(), [operatorInformation?.id])

    /**
     * Sets the history object for api interface.
     * Sets the location object for api interface
     * Listens for the changes in the location object and sets the updated location for api interface
     */
    useEffect(() => {
        Api.setHistory(history);
        Api.setLocation(history.location);
        return history.listen((location) => {
            Api.setLocation(location);
        });
    }, [history])

    /**
     * Activates or deactivates the notification logic of the application based on the state of the given permission.
     * @param {PermissionStatus} notificationPermission the new notification permission.
     * @type {(function(PermissionStatus): Promise<void>)}
     */
    const onNotificationPermissionChanged = useCallback(async (notificationPermission) => {
        switch (notificationPermission.state) {
            case "granted":
                await FirebaseNotificationService.deactivateDevice();
                await FirebaseNotificationService.activateDevice();
                break
            case "denied":
                await FirebaseNotificationService.deactivateDevice();
                break
            default:
                return;
        }
        // reload the application to apply the effect of notification
        await new Promise(r => setTimeout(r, 500));
        window.location.reload()
    }, [])

    /**
     * Registers the user into the notification platform using the FirebaseNotificationService.
     *
     * first deactivates the currently saved token of the user and registers them under a new notification token.
     * @type {(function(): void)}
     */
    const listenForNotificationPermissionChanges = useCallback(() => {
        if (!('permissions' in navigator)) return
        navigator.permissions.query({name: 'notifications'}).then((notificationPerm) => {
            notificationPerm.onchange = () => onNotificationPermissionChanged(notificationPerm);
        });
    }, [onNotificationPermissionChanged])

    /**
     * Attaches an event listeners for the storage and with each dispatch, determiens if the user should be
     * redirected to the login page.
     */
    useEffect(() => {
        listenForNotificationPermissionChanges()
    }, [listenForNotificationPermissionChanges])

    /**
     * Injects the authorization token into BizForm and production API requests.
     * The production interceptor reads the token at send time so a pre-login registration cannot freeze `undefined`.
     */
    useLayoutEffect(() => {
        injectBizFormApiInterceptors({
            request: [[
                (config) => {
                    config.headers = {
                        ...(config.headers ?? {}),
                        Authorization: authenticationToken,
                    }
                    return config;
                }
            ]],
        })
        BizProductionApiService.injectInterceptors({
            request: [
                {
                    id: 'authorization',
                    interceptor: {
                        onFulfilled: (config) => {
                            const token = CacheService.retrieveTokenAndExpiration()?.token;
                            const headers = {
                                ...(config.headers ?? {}),
                            };
                            if (typeof token === 'string' && token.length > 0) {
                                headers.Authorization = token;
                            }
                            config.headers = headers;
                            return config;
                        }
                    }
                }
            ],
        })
    }, [authenticationToken]);

    /**
     * Determines whether the user is authenticated or not and returns the appropriate view based on that.
     * @type {(function({View: *, allowGuestAccess: boolean}): (function({location: *}): JSX.Element))|*}
     */
    const authenticationGuard = useCallback(({View}) => ({location}) => {
        if (!loggedIn) {
            const maxEncodedReturnToLength = 1500;
            const fullReturnTo = location.pathname + location.search + location.hash;
            const fallbackReturnTo = location.pathname + location.hash;
            const returnTo = encodeURIComponent(fullReturnTo).length <= maxEncodedReturnToLength
                ? fullReturnTo
                : fallbackReturnTo;
            return <Redirect to={routes.auth.redirect + "?returnTo=" + encodeURIComponent(returnTo)}/>;
        } else {
            return <View/>;
        }
    }, [loggedIn])

    return (
        <>
            <StripeProvider authToken={authenticationToken}>
                <AccessLevelsProvider partnerId={partnerId} operatorId={operatorId} fallback={<LoadingView message={'Loading...'}/>}>
                    <QueryBuilderProvider
                        authorizationToken={authenticationToken}
                        applicationName={EnvService.uiComponentsApplicationName}
                    >
                        <DataGridProvider
                            authorizationToken={authenticationToken}
                            storageKey={LocalStorageService.keys.dataGridsSavedState}
                            fallback={<LoadingView message={"Loading..."}/>}
                            applicationName={EnvService.uiComponentsApplicationName}
                            skipLoadingFallback
                        >
                            <CachedPriceLevelsDictionaryProvider>
                                <PartnerConfigurationProvider>
                                <CachedIndustryProvider>
                                    <CachedIndustrySchemaProvider>
                                        <CachedPropertyGroupsProvider>
                                            <CachedCountryProvinceProvider>
                                                <PurchasingKeyProvider>
                                                    <Switch>
                                                        {
                                                            EnvService.isDevelopment &&
                                                            <Route path={routes.test} exact>
                                                                <TestView/>
                                                            </Route>
                                                        }
                                                        <Route path={routesLists.auth} exact>
                                                            <AuthenticationViews/>
                                                        </Route>
                                                        <Route path={[routes.error.notFound, routes.error.base]} exact>
                                                            <NotFoundView/>
                                                        </Route>
                                                        <Route
                                                            path={[routes.error.accessDenied, routes.error.accessDeniedForSection]}
                                                            exact>
                                                            <AccessDeniedView root/>
                                                        </Route>
                                                        <Route path={[routes.error.serverError, routes.error.serverErrorForSection]}
                                                               exact>
                                                            <ServerErrorView root/>
                                                        </Route>
                                                        <Route path={[...routesLists.print, ...routesLists.printWithServerErrorSection, ...routesLists.printWithAccessDeniedSection,]} exact>
                                                            {authenticationGuard({View: PrintViews})}
                                                        </Route>
                                                        <Route path={routes.landing}>
                                                            {authenticationGuard({View: PanelViews})}
                                                        </Route>
                                                        <Redirect to={routes.error.notFound}/>
                                                    </Switch>
                                                </PurchasingKeyProvider>
                                            </CachedCountryProvinceProvider>
                                        </CachedPropertyGroupsProvider>
                                        <ConfirmationDialog/>
                                        <ExportingDialog/>
                                    </CachedIndustrySchemaProvider>
                                </CachedIndustryProvider>
                                </PartnerConfigurationProvider>
                            </CachedPriceLevelsDictionaryProvider>
                        </DataGridProvider>
                    </QueryBuilderProvider>
                </AccessLevelsProvider>
            </StripeProvider>
        </>
    );
}

export default ApplicationViews;
